import { t } from "@travily/i18n";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useSortBy } from "react-instantsearch";

export interface SearchSortOption {
  label: string;
  value: string;
}

export interface SearchSortValue {
  currentValue: string;
  options: SearchSortOption[];
  refine: (value: string) => void;
}

/** Builds a page's sort provider: a single `useSortBy` instance shared between
 *  the SearchBar sort control (desktop) and the results heading menu (mobile).
 *  The provider must render inside `<InstantSearch>` and wrap both the top-bar
 *  SearchBar slot and the results content. Keep the page's `SORT_KEYS` next to
 *  its `// i18n-tasks-use` comments — they are load-bearing for lint-i18n. */
export function createSearchSortContext(
  name: string,
  sortKeys: readonly { labelKey: string; value: string }[],
): { SortProvider: (props: { children: ReactNode }) => ReactNode; useSort: () => SearchSortValue } {
  const Context = createContext<SearchSortValue | null>(null);

  function SortProvider({ children }: { children: ReactNode }) {
    const sortItems = useMemo(() => sortKeys.map(({ value, labelKey }) => ({ value, label: t(labelKey) })), []);
    const sortBy = useSortBy({ items: sortItems });
    const value = useMemo<SearchSortValue>(
      () => ({
        currentValue: sortBy.currentRefinement,
        refine: sortBy.refine,
        options: sortBy.options.map((opt) => ({ value: opt.value, label: opt.label })),
      }),
      [sortBy.currentRefinement, sortBy.refine, sortBy.options],
    );
    return <Context value={value}>{children}</Context>;
  }

  function useSort(): SearchSortValue {
    const ctx = useContext(Context);
    if (!ctx) throw new Error(`useSort requires a ${name} SortProvider ancestor`);
    return ctx;
  }

  return { SortProvider, useSort };
}
